import { TZDate } from '@date-fns/tz';

export type TimeZone = 'America/Sao_Paulo' | 'UTC';

/** Fuso de referência do UYZY para instantes (U14). */
export const SAO_PAULO_TZ: TimeZone = 'America/Sao_Paulo';

/** Data civil (sem fuso, sem hora) no formato ISO `AAAA-MM-DD`. */
export type CivilDate = string;

export interface CivilParts {
  readonly year: number;
  readonly month: number;
  readonly day: number;
}

const CIVIL = /^(\d{4})-(\d{2})-(\d{2})$/;
const CIVIL_PREFIX = /^(\d{4}-\d{2}-\d{2})(?:$|T)/;
const DISPLAY = /^(\d{2})\/(\d{2})\/(\d{4})$/;
const HAS_OFFSET = /(?:Z|[+-]\d{2}:?\d{2})$/i;
const WALL_TIME = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3})\d*)?)?$/;

function pad(value: number, length = 2): string {
  return String(value).padStart(length, '0');
}

function daysInMonth(year: number, month: number): number {
  if (month === 2) return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0 ? 29 : 28;
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
}

function validParts(year: number, month: number, day: number): CivilParts | null {
  if (year < 1 || month < 1 || month > 12 || day < 1) return null;
  return day <= daysInMonth(year, month) ? { year, month, day } : null;
}

/** Lê `AAAA-MM-DD` em partes, conferindo o calendário (29/02 só em ano bissexto). Nunca usa `Date`. */
export function parseCivilDate(value: string): CivilParts | null {
  const match = CIVIL.exec(value);
  if (!match) return null;
  return validParts(Number(match[1]), Number(match[2]), Number(match[3]));
}

export function isCivilDate(value: string): boolean {
  return parseCivilDate(value) !== null;
}

/** Monta `AAAA-MM-DD` a partir das partes. */
export function toCivilDate({ year, month, day }: CivilParts): CivilDate {
  return `${pad(year, 4)}-${pad(month)}-${pad(day)}`;
}

/**
 * A data como foi escrita num valor do legado: `2026-08-14`, `2026-08-14T00:00:00-03:00` →
 * `2026-08-14`. Não converte fuso; para o dia de um instante, use {@link civilDateAt}.
 */
export function civilDateOf(value: string): CivilDate | null {
  const prefix = CIVIL_PREFIX.exec(value.trim())?.[1];
  return prefix !== undefined && isCivilDate(prefix) ? prefix : null;
}

/** `14/08/2026` → `2026-08-14`, ou `null` se incompleto ou fora do calendário. */
export function parseDisplayDate(text: string): CivilDate | null {
  const match = DISPLAY.exec(text.trim());
  if (!match) return null;
  const parts = validParts(Number(match[3]), Number(match[2]), Number(match[1]));
  return parts === null ? null : toCivilDate(parts);
}

/** `2026-08-14` → `14/08/2026`, a ordem de digitação do `DateField` em qualquer idioma. */
export function toDisplayDate(value: CivilDate): string | null {
  const parts = parseCivilDate(value);
  return parts === null ? null : `${pad(parts.day)}/${pad(parts.month)}/${pad(parts.year, 4)}`;
}

/**
 * Locale do Intl para datas. `en` vira `en-GB` para manter dia/mês/ano, a mesma ordem do campo
 * de digitação; os outros passam como estão.
 */
export function intlDateLocale(locale: string): string {
  return locale === 'en' ? 'en-GB' : locale;
}

export interface FormatDateOptions {
  readonly locale?: string;
  readonly month?: '2-digit' | 'short' | 'long';
}

/**
 * Formata uma data civil: `2026-08-14` → `14/08/2026`. Formata em UTC sobre `Date.UTC`, então o
 * dia nunca escorrega (o bug do `@uy3/patterns` que U14 não copia).
 */
export function formatCivilDate(value: string, options: FormatDateOptions = {}): string | null {
  const civil = civilDateOf(value);
  const parts = civil === null ? null : parseCivilDate(civil);
  if (parts === null) return null;
  return new Intl.DateTimeFormat(intlDateLocale(options.locale ?? 'pt-BR'), {
    timeZone: 'UTC',
    day: '2-digit',
    month: options.month ?? '2-digit',
    year: 'numeric',
  }).format(Date.UTC(parts.year, parts.month - 1, parts.day));
}

/**
 * Lê um instante. Com `Z` ou offset, é o instante escrito. Sem offset (`2026-08-14T10:30:00`),
 * é hora de parede de São Paulo, como o legado grava.
 */
export function parseInstant(value: string): Date | null {
  const text = value.trim();
  if (HAS_OFFSET.test(text) && text.includes('T')) {
    const instant = new Date(text);
    return Number.isNaN(instant.getTime()) ? null : instant;
  }
  const wall = WALL_TIME.exec(text);
  if (!wall) return null;
  const [, y, mo, d, h, mi, s, ms] = wall;
  const parts = validParts(Number(y), Number(mo), Number(d));
  if (parts === null) return null;
  const wallClock = new TZDate(
    parts.year,
    parts.month - 1,
    parts.day,
    Number(h),
    Number(mi),
    Number(s ?? 0),
    Number((ms ?? '0').padEnd(3, '0')),
    SAO_PAULO_TZ,
  );
  return new Date(wallClock.getTime());
}

export interface FormatInstantOptions {
  readonly locale?: string;
  readonly timeZone?: TimeZone;
  readonly seconds?: boolean;
  /** `false` mostra só a data do instante no fuso. */
  readonly time?: boolean;
}

/** Formata um instante em São Paulo: `2026-08-14T13:30:00Z` → `14/08/2026, 10:30`. */
export function formatInstant(value: string | Date, options: FormatInstantOptions = {}): string | null {
  const instant = typeof value === 'string' ? parseInstant(value) : value;
  if (instant === null || Number.isNaN(instant.getTime())) return null;
  const time = options.time ?? true;
  return new Intl.DateTimeFormat(intlDateLocale(options.locale ?? 'pt-BR'), {
    timeZone: options.timeZone ?? SAO_PAULO_TZ,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    ...(time ? { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' } : {}),
    ...(time && options.seconds === true ? { second: '2-digit' } : {}),
  }).format(instant);
}

/** O dia civil de um instante num fuso (padrão São Paulo). Quem chama passa o `now`. */
export function civilDateAt(instant: Date, timeZone: TimeZone = SAO_PAULO_TZ): CivilDate {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(instant);
  const get = (type: string) => Number(parts.find((part) => part.type === type)?.value);
  return toCivilDate({ year: get('year'), month: get('month'), day: get('day') });
}

/**
 * Ponte para widgets de calendário que só falam `Date`: meio-dia local do dia civil, longe de
 * qualquer virada de horário de verão. É a única conversão civil → `Date` permitida (U14).
 */
export function civilToCalendarDate(value: CivilDate): Date | null {
  const parts = parseCivilDate(value);
  if (parts === null) return null;
  const date = new Date(0);
  date.setFullYear(parts.year, parts.month - 1, parts.day);
  date.setHours(12, 0, 0, 0);
  return date;
}

/** Volta do widget: lê ano, mês e dia locais do `Date` que o calendário devolveu. */
export function calendarDateToCivil(date: Date): CivilDate {
  return toCivilDate({ year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() });
}
